/**
 * The AI model catalogue's shared words (TMMPDM-0104): a model's name, and the refusals the
 * catalogue dialogs and Verify share. Every value read from a refusal is the server's `details`,
 * shown as sent: `tiers` (AI_MODEL_IN_USE) and `reason` (AI_PROVIDER_UNAVAILABLE). TMMPDM-0105: the
 * `AUTH` reason's words, AI_MODEL_NOT_SUPPORTED's reasons, and an effort's words.
 */
import { ApiError, errorCopy } from '@/api/errors';
import type { AiModelCatalogRow } from '@/api/tmm/types';
import type { useCopy } from '@/hooks/useCopy';

type Translate = ReturnType<typeof useCopy>['t'];

/** A refusal line plus, for AI_PROVIDER_UNAVAILABLE, the server's reason in small text. */
export interface CatalogRefusal {
  text: string;
  reason?: string;
}

/** A catalogue model's name: `displayName` when sent, else `modelId`. */
export function modelName(row: AiModelCatalogRow): string {
  return typeof row.displayName === 'string' && row.displayName.trim() !== ''
    ? row.displayName
    : row.modelId;
}

/** The server's `details.<key>` when it is a list of strings, else empty. */
function detailList(err: ApiError, key: string): string[] {
  const raw = err.details.backend?.[key];
  return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === 'string') : [];
}

function tierWord(t: Translate, tier: string): string {
  if (tier === 'FAST') return t('ai.catalog.tier.FAST', 'Fast');
  if (tier === 'DEEP') return t('ai.catalog.tier.DEEP', 'Deep');
  return tier;
}

/** "The Fast tier uses this model…", from the server's `details.tiers`; a tierless line without them. */
export function inUseCopy(t: Translate, err: ApiError): string {
  const tiers = detailList(err, 'tiers').map((tier) => tierWord(t, tier));
  if (tiers.length === 0) {
    return t(
      'ai.catalog.inUseNoTiers',
      'A tier uses this model. Assign another model there first.',
    );
  }
  return t(
    'ai.catalog.inUse',
    'The {{tiers}} tier uses this model. Assign another model there first.',
    { tiers: tiers.join(t('ai.catalog.and', ' and ')) },
  );
}

/**
 * AI_PROVIDER_UNAVAILABLE: the copy, and `details.reason` as sent when present. TMMPDM-0105: the
 * reason `AUTH` (the API key is wrong) has its own words.
 */
export function unavailableRefusal(t: Translate, err: ApiError): CatalogRefusal {
  const raw = err.details.backend?.['reason'];
  if (raw === 'AUTH') {
    return {
      text: t(
        'ai.catalog.unavailableAuth',
        'Anthropic refused our API key. Ask the platform operator to check the key; nothing was saved.',
      ),
    };
  }
  const text = t(
    'ai.catalog.unavailable',
    "We couldn't reach Anthropic to check the model. Nothing was saved. Try again in a moment.",
  );
  return typeof raw === 'string' && raw.trim() !== '' ? { text, reason: raw } : { text };
}

/**
 * AI_MODEL_NOT_SUPPORTED (TMMAPI-0172/0174): the words for `details.reason` `RETIRED`, `NO_EFFORT`
 * or `NO_STRUCTURED_OUTPUT`, or null for a reason this build doesn't name.
 */
export function notSupportedCopy(t: Translate, err: ApiError): string | null {
  const reason = err.details.backend?.['reason'];
  if (reason === 'RETIRED') {
    return t(
      'ai.catalog.notSupported.RETIRED',
      "Anthropic has retired that model. It can't be added.",
    );
  }
  if (reason === 'NO_EFFORT') {
    return t(
      'ai.catalog.notSupported.NO_EFFORT',
      "That model doesn't take an effort setting, which every AI call sends. It can't be added.",
    );
  }
  if (reason === 'NO_STRUCTURED_OUTPUT') {
    return t(
      'ai.catalog.notSupported.NO_STRUCTURED_OUTPUT',
      "That model doesn't take structured outputs, which every AI call needs. It can't be added.",
    );
  }
  return null;
}

/** An effort's words (TMMPDM-0104); one this build doesn't name is shown as sent. */
export function effortLabel(t: Translate, effort: string): string {
  switch (effort) {
    case 'LOW':
      return t('ai.effort.LOW', 'Low');
    case 'MEDIUM':
      return t('ai.effort.MEDIUM', 'Medium');
    case 'HIGH':
      return t('ai.effort.HIGH', 'High');
    case 'XHIGH':
      return t('ai.effort.XHIGH', 'Extra high');
    case 'MAX':
      return t('ai.effort.MAX', 'Max');
    default:
      return effort;
  }
}

/**
 * A Verify that failed (TMMPDM-0104/0105, shared with the tier tabs' "Verify this model first"
 * TMMPDM-0108): the words, and whether the model wasn't found (the caller re-reads).
 */
export function verifyFailure(
  t: Translate,
  err: unknown,
): { refusal: CatalogRefusal; notFound: boolean } {
  const code = err instanceof ApiError ? String(err.code) : '';
  if (code === 'AI_MODEL_NOT_AT_PROVIDER') {
    return {
      refusal: {
        text: t(
          'ai.catalog.goneAtProvider',
          "Anthropic no longer knows this model. It hasn't been retired; retire it when nothing uses it.",
        ),
      },
      notFound: false,
    };
  }
  if (code === 'AI_PROVIDER_UNAVAILABLE' && err instanceof ApiError) {
    return { refusal: unavailableRefusal(t, err), notFound: false };
  }
  if (code === 'NOT_FOUND' || (err instanceof ApiError && err.status === 404)) {
    return {
      refusal: {
        text: errorCopy(err, t, t('ai.catalog.notFound', "That model couldn't be found.")),
      },
      notFound: true,
    };
  }
  return {
    refusal: {
      text: errorCopy(err, t, t('ai.catalog.verifyError', "Couldn't check the model.")),
    },
    notFound: false,
  };
}
